import { useQuery } from '@tanstack/react-query';
import { portalDb, type PortalBrandingRow, type PortalCompanyRow } from './portalClient';

const SLUG = /^[A-Za-z0-9_-]+$/;

/**
 * The company behind a portal slug, visible only when the signed-in customer
 * is linked to it (the view joins customer_users). Null = unknown slug or not
 * linked; the caller shows the same "not linked" card for both.
 */
export function usePortalCompany(slug: string | undefined) {
  return useQuery({
    queryKey: ['portal', 'company', slug],
    enabled: !!slug && SLUG.test(slug),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PortalCompanyRow | null> => {
      const { data, error } = await portalDb
        .from('portal_company')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return (data as PortalCompanyRow | null) ?? null;
    },
  });
}

/** Pre-login branding (anon-callable RPC). Empty result = unknown slug. */
export function usePortalBranding(slug: string | undefined) {
  return useQuery({
    queryKey: ['portal', 'branding', slug],
    enabled: !!slug && SLUG.test(slug),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PortalBrandingRow | null> => {
      const { data, error } = await portalDb.rpc('portal_public_branding', { p_slug: slug });
      if (error) throw error;
      const rows = (data ?? []) as PortalBrandingRow[];
      return rows[0] ?? null;
    },
  });
}
